import { UsageCostCell, UsageTotals } from './LlmUsage';
/**
 * Observer "Activity Report" tab: structured HTML (MUI), same aggregates as the Observer API.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import {
  Sms as SmsIcon,
  OpenInNew as OpenInNewIcon,
  Psychology as PsychologyIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import ObserverApi, { ActivityReportMspSection, ActivityReportResponse } from '../apis/observerApi';
import ObserverScopeFilters from './ObserverScopeFilters';
import { useObserverScope } from '../hooks/useObserverScope';
import { formatObserverTimestamp } from '../utils/threadUtils';
import {
  activityReportTopThreadToActionRow,
  observerThreadUserExperienceTitle,
  observerThreadUserExperienceUrl,
  type ObserverThreadActionRow,
} from '../utils/observerThreadActionRow';

interface MSP {
  id: number;
  name: string;
}

export interface ActivityReportThreadActionHandlers {
  onViewMessages: (thread: ObserverThreadActionRow) => void | Promise<void>;
  onViewAuditLogs: (thread: ObserverThreadActionRow) => void;
  onViewUsage: (thread: ObserverThreadActionRow) => void | Promise<void>;
}

interface ActivityReportPanelProps {
  msps: MSP[];
  /** When set, top threads get the same action buttons as AI Thread Observer (messages, audit, LangSmith, usage, optional ticket). */
  threadActionHandlers?: ActivityReportThreadActionHandlers;
}

function utcIsoDate(offsetDays = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const actionBtnSx = { minWidth: 'auto', px: 1 } as const;

function formatThreadType(threadType: ActivityReportMspSection['topThreads'][number]['thread_type']): string {
  switch (threadType) {
    case 'chat_assistant':
      return 'Chat Agent';
    case 'ticket_assistant':
      return 'Ticket Assistant';
    default:
      return '—';
  }
}

function userExperienceLink(
  t: ActivityReportMspSection['topThreads'][number],
  ticketId: string | null,
): { href: string; label: string } | null {
  if (t.thread_type === 'ticket_assistant' && ticketId) {
    return {
      href: `${window.location.origin}/supervisor/tickets/${encodeURIComponent(ticketId)}`,
      label: ticketId,
    };
  }
  if (t.thread_type === 'chat_assistant') {
    return {
      href: `${window.location.origin}/chat-assistant/${encodeURIComponent(String(t.ai_thread_id))}`,
      label: 'Chat Agent',
    };
  }
  return null;
}

const ActivityReportPanel: React.FC<ActivityReportPanelProps> = ({ msps, threadActionHandlers }) => {
  const api = useMemo(() => new ObserverApi(), []);
  const [startDateUtc, setStartDateUtc] = useState(() => utcIsoDate(-1));
  const [endDateUtc, setEndDateUtc] = useState(() => utcIsoDate());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ActivityReportResponse | null>(null);
  const {
    selectedMspIds,
    selectedUserIds,
    users,
    usersLoading,
    usersFetchError,
    setUsersFetchError,
    mspIdsForRequest,
    userIdsForRequest,
    omitInternalUsers,
    omitAutoagent,
    handleMspChange,
    handleUserChange,
    setOmitInternalUsers,
    setOmitAutoagent,
  } = useObserverScope(msps, api, {
    defaultOmitInternalUsers: true,
    defaultOmitAutoagent: true,
  });

  const loadReport = useCallback(async () => {
    const start = startDateUtc.trim();
    const end = endDateUtc.trim();
    if (!ISO_DATE.test(start) || !ISO_DATE.test(end)) {
      setError('Use UTC calendar dates in YYYY-MM-DD format for start and end.');
      return;
    }
    if (start > end) {
      setError('Start date must be the same as or before the end date.');
      return;
    }
    if (mspIdsForRequest.length === 0) {
      setError('Select at least one MSP.');
      return;
    }
    if (userIdsForRequest === null) {
      setError('Select at least one user.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await api.getActivityReport({
        startDate: start,
        endDate: end,
        mspIds: mspIdsForRequest,
        userIds: userIdsForRequest,
      });
      setData(res);
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : 'Could not load Activity Report.');
    } finally {
      setLoading(false);
    }
  }, [api, startDateUtc, endDateUtc, mspIdsForRequest, userIdsForRequest]);

  const rangeTitle =
    data && data.startDateUtc === data.endDateUtc
      ? `${data.startDateUtc} (UTC)`
      : data
        ? `${data.startDateUtc} → ${data.endDateUtc} (UTC)`
        : '';

  const langsmithThreadUrl = (aiThreadId: number) => {
    const projectId =
      process.env.REACT_APP_LANGSMITH_PROJECT_ID || '20f0ccc5-d50a-46f8-8f72-3e8470aa7a3c';
    return `https://smith.langchain.com/o/44975c4f-7463-4174-a1b2-7390e12b2622/projects/p/${projectId}/t/${aiThreadId}`;
  };

  const renderAnalysis = (t: ActivityReportMspSection['topThreads'][number]) => {
    const analysis = t.analysis;
    if (!analysis) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }

    const customerLabel =
      analysis.m365_customer_name
      || analysis.customer_name
      || (analysis.m365_customer_id != null
        ? `customer_id ${analysis.m365_customer_id}`
        : analysis.customer_id != null
          ? `customer_id ${analysis.customer_id}`
          : 'unknown customer');
    const integrationLabel =
      analysis.productivity_integration_label
      || (Array.isArray(analysis.productivity_integrations) && analysis.productivity_integrations.length
        ? analysis.productivity_integrations.filter(Boolean).join(' · ')
        : analysis.m365_integration
          ? 'M365'
          : analysis.gworkspace_integration
            ? 'GWorkspace'
            : 'No integration');

    return (
      <Box sx={{ minWidth: 320, maxWidth: 520 }}>
        <Typography variant="body2">
          <strong>M365 / GWorkspace integration for {customerLabel}:</strong>{' '}
          {integrationLabel}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>Summary:</strong> {analysis.summary}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>Expectation:</strong> {analysis.expectation_assessment}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>Potential improvements:</strong>{' '}
          {(analysis.potential_improvements || []).join(' ') || '—'}
        </Typography>
        {(analysis.audit_errors || []).length > 0 ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2">
              <strong>Audit errors:</strong>
            </Typography>
            {(analysis.audit_errors || []).map((err, idx) => (
              <Typography
                key={`${t.ai_thread_id}-audit-error-${idx}`}
                variant="body2"
                color="error"
                component="div"
              >
                {err.tool_name || 'Unknown tool'}:{' '}
                {err.error_message || err.output_error || err.reasoning || err.status || 'Error'}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Audit errors: none in range.
          </Typography>
        )}
      </Box>
    );
  };

  const renderSection = (s: ActivityReportMspSection) => (
    <Card
      key={s.mspId}
      variant="outlined"
      sx={{ mb: 2, borderColor: 'divider' }}
      component="section"
    >
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          {s.mspName}{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            (msp_id {s.mspId})
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Threads with activity: <strong>{s.threadCount}</strong>
          {' · '}With ticket: <strong>{s.threadsWithTicket}</strong>
          {' · '}Assistant (no ticket): <strong>{s.threadsAssistantOnly}</strong>
          <br />
          Messages (excluding assistant replies): <strong>{s.messageCount}</strong>
          {' · '}Distinct callers: <strong>{s.distinctHumanUsers}</strong>
          {' · '}Tool audit actions: <strong>{s.toolActions}</strong>
        </Typography>
        <UsageTotals usage={s.usage} />
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Top threads by last message (most recent first)
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Thread ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>User email</TableCell>
                <TableCell align="right">Msgs</TableCell>
                <TableCell>Last message</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell>Analysis</TableCell>
                {threadActionHandlers ? <TableCell>Actions</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {(s.topThreads || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={threadActionHandlers ? 10 : 9}>
                    <Typography variant="body2" color="text.secondary">
                      No ranked threads for this range.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                s.topThreads.map((t) => {
                  const ticketTrimmed =
                    t.ticket_id != null && String(t.ticket_id).trim() !== ''
                      ? String(t.ticket_id).trim()
                      : null;
                  const actionRow = activityReportTopThreadToActionRow(t);
                  const handlers = threadActionHandlers;
                  const experienceLink = userExperienceLink(t, ticketTrimmed);
                  const userExperienceActionUrl = observerThreadUserExperienceUrl(actionRow);
                  const userExperienceActionTitle = observerThreadUserExperienceTitle(actionRow);

                  return (
                    <TableRow key={t.ai_thread_id}>
                      <TableCell>
                        {handlers ? (
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            fontWeight={500}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handlers.onViewMessages(actionRow);
                            }}
                            sx={{ p: 0, border: 0, cursor: 'pointer', verticalAlign: 'baseline' }}
                          >
                            {t.ai_thread_id}
                          </Link>
                        ) : (
                          t.ai_thread_id
                        )}
                      </TableCell>
                      <TableCell>{formatThreadType(t.thread_type)}</TableCell>
                      <TableCell>
                        {experienceLink ? (
                          <Link
                            href={experienceLink.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            fontWeight={500}
                          >
                            {experienceLink.label}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{t.thread_name ?? '—'}</TableCell>
                      <TableCell>{t.user_email?.trim() || '—'}</TableCell>
                      <TableCell align="right">{t.msgs_on_day}</TableCell>
                      <TableCell>
                        {t.last_message_at
                          ? formatObserverTimestamp(t.last_message_at)
                          : '—'}
                      </TableCell>
                      <UsageCostCell usage={t.usage} onClick={handlers ? () => { void handlers.onViewUsage(actionRow); } : undefined} />
                      <TableCell>{renderAnalysis(t)}</TableCell>
                      {handlers ? (
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={actionBtnSx}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handlers.onViewMessages(actionRow);
                              }}
                              title="Read Messages"
                            >
                              <SmsIcon fontSize="small" />
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={actionBtnSx}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlers.onViewAuditLogs(actionRow);
                              }}
                              title="View Audit Logs"
                            >
                              <SecurityIcon fontSize="small" />
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={actionBtnSx}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(langsmithThreadUrl(t.ai_thread_id), '_blank');
                              }}
                              title="View in LangSmith"
                            >
                              <PsychologyIcon fontSize="small" />
                            </Button>
                            {userExperienceActionUrl ? (
                              <Button
                                size="small"
                                variant="outlined"
                                sx={actionBtnSx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(userExperienceActionUrl, '_blank');
                                }}
                                title={userExperienceActionTitle}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </Button>
                            ) : null}
                          </Box>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  return (
    <Box component="article" sx={{ maxWidth: 1400 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Inclusive UTC date range (defaults: previous UTC day for start, today UTC for end). Same aggregates as the
        Observer API; non-deleted threads. Activity follows the selected MSP and user scopes.
        Message counts exclude Theo assistant replies.
      </Alert>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <TextField
          label="Start date (UTC)"
          type="date"
          size="small"
          value={startDateUtc}
          onChange={(e) => setStartDateUtc(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="End date (UTC)"
          type="date"
          size="small"
          value={endDateUtc}
          onChange={(e) => setEndDateUtc(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        />
        <ObserverScopeFilters
          msps={msps}
          users={users}
          selectedMspIds={selectedMspIds}
          selectedUserIds={selectedUserIds}
          onMspChange={handleMspChange}
          onUserChange={handleUserChange}
          omitInternalUsers={omitInternalUsers}
          onOmitInternalUsersChange={setOmitInternalUsers}
          omitAutoagent={omitAutoagent}
          onOmitAutoagentChange={setOmitAutoagent}
          usersLoading={usersLoading}
        />
        <Button variant="contained" onClick={loadReport} disabled={loading}>
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Load Activity Report'}
        </Button>
      </Box>

      {usersFetchError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setUsersFetchError(null)}>
          {usersFetchError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {data && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Activity Report — {rangeTitle}
          </Typography>
          <UsageTotals usage={data.usage} />
          {data.sections.length === 0 ? (
            <Alert severity="info">
              No MSP had thread, message, or tool activity for this UTC date range (with current
              filters).
            </Alert>
          ) : (
            data.sections.map(renderSection)
          )}
        </Box>
      )}
    </Box>
  );
};

export default ActivityReportPanel;
